/**
 * Enhanced Duty Log Explorer - Clean Architecture
 * 
 * Uses ConfigurableBaseExplorer with clean configuration for Enhanced DLE
 * without contamination from existing implementations.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPlus } from '@fortawesome/free-solid-svg-icons';
import ConfigurableBaseExplorer from '../baseExplorer/ConfigurableBaseExplorer';
import ContextHelp from '../../../easymap/ContextHelp/ContextHelp';
import { createDutyLogConfig, DutyLogEntityEntry } from './DutyLogConfig';
import { DutyLogCreateModal } from './DutyLogCreateModal';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { extractUserInfo, UserContextInfo, DEFAULT_USER_CONTEXT } from '../../../utils/userContext';
import EnhancedLogDisplay from './EnhancedLogDisplay';
import AppModal from '../../common/AppModal';
import DutyLogExportModal from './DutyLogExportModal';
import { buildDutyLogFilterDefinitions } from './dutyLogExportFilters';
import { generateLogbookPdf, DEFAULT_REPORT_FOOTER, downloadPdf, LogbookEntry } from '../../../services/pdfReportService';
import { dutyLogService } from '../../../services/dutyLogService';
import { usePermissions } from '../../../context/PermissionContext';
import './DutyLogExplorer.scss';

interface UserContext {
  userContext: string;
  userClass: string;
  userName: string;
}

interface EnhancedDutyLogExplorerProps {
  enhancementEnabled?: boolean;
  onCrosslinkSearch?: (term: string, type: string) => void;
  crosslinkSearchEnabled?: boolean;
  initialCrosslinkSearch?: { term: string; type: string };
  initialFilters?: Record<string, any>;
  height?: number;
  width?: number;
  onClose?: () => void;
  className?: string;
  style?: React.CSSProperties;
  windowId?: string;
  onSelectionChange?: (selection: any[]) => void;
  onDataChange?: () => void;
  initialPosition?: { x: number; y: number };
  initialSize?: { width: number; height: number };
}

/**
 * Enhanced Duty Log Explorer Component
 * 
 * Provides the same UI/UX as the existing DLE but with clean architecture
 * and support for ToolBox integration with enhancement features.
 */
export const EnhancedDutyLogExplorer: React.FC<EnhancedDutyLogExplorerProps> = ({
  enhancementEnabled = false,
  onCrosslinkSearch,
  crosslinkSearchEnabled = true,
  initialCrosslinkSearch,
  initialFilters = {},
  height = window.innerWidth <= 768 ? window.innerHeight - 20 : 600,
  width = window.innerWidth <= 768 ? window.innerWidth - 20 : 800,
  onClose,
  className,
  style,
  windowId,
  onSelectionChange,
  onDataChange,
  initialPosition,
  initialSize
}) => {
  // EDL Modal State
  const [edlModalOpen, setEdlModalOpen] = useState(false);
  const [edlEntity, setEdlEntity] = useState<DutyLogEntityEntry | null>(null);
  const [edlEnhancement, setEdlEnhancement] = useState<any>(null);
  const [edlShowOriginal, setEdlShowOriginal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<Record<string, any>>({});
  // Track the active search term so export respects the search bar (#624).
  const [currentSearch, setCurrentSearch] = useState<string>('');

  // Crosslink search state
  const [crosslinkSearch, setCrosslinkSearch] = useState<{ term: string; type: string } | null>(initialCrosslinkSearch || null);

  useEffect(() => {
    const handleExportRequest = () => setShowExportModal(true);
    window.addEventListener('dle:export-request', handleExportRequest);
    return () => window.removeEventListener('dle:export-request', handleExportRequest);
  }, []);

  const hasSignificantEnhancement = (original: string, enhanced: string): boolean => {
    if (!original || !enhanced) return false;
    const normOriginal = original.trim().toLowerCase();
    const normEnhanced = enhanced.trim().toLowerCase();
    if (normOriginal === normEnhanced) return false;
    const lenDiff = Math.abs(normEnhanced.length - normOriginal.length);
    if (lenDiff > normOriginal.length * 0.05 || lenDiff > 20) return true;
    let diffs = 0;
    const minLen = Math.min(normOriginal.length, normEnhanced.length);
    for (let i = 0; i < minLen; i++) {
      if (normOriginal[i] !== normEnhanced[i]) diffs++;
      if (diffs > 10) return true;
    }
    return diffs > 5;
  };

  // Handler for View EDL button
  const handleViewEDL = async (entity: DutyLogEntityEntry) => {
    setEdlEntity(entity);
    setEdlModalOpen(true);
    setEdlShowOriginal(false);
    try {
      const { DutyLogEnhancer } = await import('./EnhancementEngine');
      const enhancer = new DutyLogEnhancer();
      const result = enhancer.enhanceLogEntryWithCrosslinking(
        entity.body,
        entity.logDate ? new Date(entity.logDate).getFullYear() : undefined
      );
      setEdlEnhancement(result);
    } catch (error) {
      setEdlEnhancement(null);
    }
  };

  // #693: use the shared mapping so the export applies the same per-field
  // operators as the list (dropdowns -> equals, not a forced `contains`).
  const buildFilterDefinitions = () => buildDutyLogFilterDefinitions(currentFilters);

  const handleExport = async (format: 'csv' | 'pdf' | 'legal-pdf') => {
    setExporting(true);
    try {
      const filters = buildFilterDefinitions();
      const dateStr = new Date().toISOString().slice(0, 10);

      if (format === 'legal-pdf') {
        const { DutyLogEnhancer } = await import('./EnhancementEngine');
        const enhancer = new DutyLogEnhancer();
        const response = await dutyLogService.fetchData({
          page: 0, pageSize: 10000,
          sort: [{ field: 'logDate', direction: 'desc' }],
          filters,
          search: currentSearch?.trim() ? currentSearch.trim() : undefined
        });
        const entries: LogbookEntry[] = response.data.map(entry => {
          const year = entry.logDate ? new Date(entry.logDate).getFullYear() : undefined;
          const enhanced = enhancer.enhanceLogEntryWithCrosslinking(entry.body, year);
          return {
            date: new Date(entry.logDate).toLocaleString('en-CA', {
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit', hour12: false
            }),
            author: `Duty Officer (${entry.logContext}-${entry.logClass}): ${entry.logUser}`,
            body: entry.body,
            additionalContext: enhanced.enhanced_body.replace(/<[^>]*>/g, '').trim() !== entry.body.trim()
              ? enhanced.enhanced_body.replace(/<[^>]*>/g, '').trim()
              : undefined
          };
        });
        const blob = await generateLogbookPdf({ title: 'Duty Log Report (Legal)', entries, footer: DEFAULT_REPORT_FOOTER });
        downloadPdf(blob, `duty-logs-legal-${dateStr}.pdf`);
      } else {
        const blob = await dutyLogService.exportData(format, filters, currentSearch);
        if (format === 'csv') {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `duty-logs-export-${dateStr}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        } else {
          downloadPdf(blob, `duty-logs-${dateStr}.pdf`);
        }
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(false);
      setShowExportModal(false);
    }
  };

  const { userInfo, isFetchingUserInfo, isLoggedIn } = useFusionAuth();
  const { canDo } = usePermissions();
  const [userContext, setUserContext] = useState<UserContextInfo>(DEFAULT_USER_CONTEXT);

  useEffect(() => {
    if (!isFetchingUserInfo && userInfo) {
      const contextInfo = extractUserInfo(userInfo);
      setUserContext(contextInfo);
    }
  }, [userInfo, isFetchingUserInfo]);

  const config = useMemo(() => createDutyLogConfig(userContext), [userContext]);

  const handleCrosslinkSearchChange = React.useCallback((search: { term: string; type: string } | null) => {
    if (search && search.term) {
      setCrosslinkSearch(prev => {
        if (!prev || prev.term !== search.term || prev.type !== search.type) {
          return search;
        }
        return prev;
      });
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
      }
      if (crosslinkSearchEnabled && onCrosslinkSearch) {
        onCrosslinkSearch(search.term, search.type);
      }
      // No refreshKey bump — that would remount CBE and discard the active
      // search/filter state. CBE's effect picks up initialCrosslinkSearch
      // and updates its internal state, triggering a fetch with all dimensions.
    } else {
      setCrosslinkSearch(null);
    }
  }, [crosslinkSearchEnabled, onCrosslinkSearch]);

  const [dynamicZIndex, setDynamicZIndex] = useState<number | undefined>(undefined);
  const handleZIndexUpdate = React.useCallback((zIndex: number) => {
    setDynamicZIndex(zIndex);
  }, []);

  const [refreshKey, setRefreshKey] = useState(0);

  // Prevent rendering until user context is properly initialized
  if (userContext === DEFAULT_USER_CONTEXT) {
    return <div>Loading user context...</div>; // Or a spinner
  }

  // Merge the original style with dynamic z-index if available
  const mergedStyle = dynamicZIndex !== undefined
    ? { ...style, zIndex: dynamicZIndex }
    : style;

  return (
    <>
      <DutyLogCreateModal onCreated={() => { onDataChange?.(); setRefreshKey(prev => prev + 1); }} />

      <div className={`enhanced-duty-log-explorer ${className || ''}`} style={mergedStyle}>
        <ConfigurableBaseExplorer<DutyLogEntityEntry>
          key={`dle-explorer-${refreshKey}`}
          config={config}
          customToolbar={
            <>
              {canDo('duty_logs', 'create') && (
                <button
                  type="button"
                  className="btn-primary"
                  title="New Duty Log"
                  aria-label="New Duty Log"
                  onClick={() => window.dispatchEvent(new CustomEvent('dle:create-request'))}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span className="btn-label">New Duty Log</span>
                </button>
              )}
              {canDo('duty_logs', 'export') && (
                <button
                  type="button"
                  className="btn-primary"
                  title="Export"
                  aria-label="Export"
                  onClick={() => window.dispatchEvent(new CustomEvent('dle:export-request'))}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span className="btn-label">Export</span>
                </button>
              )}
            </>
          }
          help={<ContextHelp title='Help with Duty Logs' context='duty-log-help' />}
          isAuthenticated={isLoggedIn}
          onCrosslinkSearchChange={handleCrosslinkSearchChange}
          initialCrosslinkSearch={crosslinkSearch || undefined}
          isWindowed={true}
          windowTitle="Duty Log Explorer"
          onZIndexUpdate={handleZIndexUpdate}
          onActiveFiltersChange={setCurrentFilters}
          onSearchChange={setCurrentSearch}
          initialPosition={initialPosition || (window.innerWidth <= 768 ? { x: 10, y: 10 } : { x: 100, y: 100 })}
          initialSize={initialSize || { width, height }}
          resizable={true}
          draggable={true}
          showCloseButton={true}
          showMinimizeButton={true}
          onWindowClose={onClose}
          onEntitySelect={onSelectionChange ? (entity) => onSelectionChange([entity]) : undefined}
          onEntityCreate={onDataChange ? () => onDataChange() : undefined}
          className="enhanced-duty-log-explorer-configured"
          testId="enhanced-duty-log-explorer"
          onEntityEdit={handleViewEDL}
        >
          {/* Crosslink search indicator rendered as a child of the explorer */}
          {crosslinkSearch && crosslinkSearch.term && (
            <div
              id="crosslink-search-indicator"
              style={{
                margin: '0 12px 8px 12px',
                padding: '4px 12px',
                background: '#ffe7c2',
                border: '2px solid #e67e22',
                borderRadius: 4,
                fontSize: 16,
                color: '#b35400',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                maxWidth: 'calc(100% - 24px)',
                boxShadow: '0 0 0 2px #e67e22'
              }}
            >
              <span style={{ flex: 1 }}>
                Crosslink search: <span>{crosslinkSearch.term}</span>
                {crosslinkSearch.type ? (
                  <span style={{ color: '#888', fontWeight: 400 }}> ({crosslinkSearch.type})</span>
                ) : null}
              </span>
              <button
                type="button"
                onClick={() => {
                  // Clear the parent's crosslink state only. CBE picks this up
                  // via its initialCrosslinkSearch effect and clears its own
                  // crosslinkSearch state, preserving the active search/filters.
                  setCrosslinkSearch(null);
                }}
                title="Clear crosslink"
                aria-label="Clear crosslink"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#b35400',
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: '0 4px',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          )}
        </ConfigurableBaseExplorer>

        <DutyLogExportModal
          isOpen={showExportModal}
          exporting={exporting}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
        />

        {/* EDL Modal - EnhancedLogDisplay */}
        <AppModal
          isOpen={edlModalOpen && !!edlEntity}
          onClose={() => { setEdlModalOpen(false); setEdlEntity(null); setEdlEnhancement(null); }}
          width={700}
          maxHeight="90vh"
          header="Enhanced Dutylog Viewer"
        >
          {edlEnhancement ? (
                  <>
                    {/* Regular Duty Log Metadata */}
                    <div style={{ marginBottom: '16px', background: 'var(--em3-color-background-panel)', borderRadius: '6px', padding: '12px', border: '1px solid var(--em3-color-border)', color: 'var(--em3-color-text-primary)' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: 'var(--em3-color-text-primary)' }}>Duty Log Metadata</h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', fontSize: '14px' }}>
                        <div><strong>User:</strong> {edlEntity?.logUser ?? 'N/A'}</div>
                        <div><strong>Role:</strong> {edlEntity?.logContext ?? 'N/A'}</div>
                        <div><strong>Region:</strong> {edlEntity?.logClass ?? 'N/A'}</div>
                        <div><strong>Date:</strong> {edlEntity?.logDate ? new Date(edlEntity.logDate).toLocaleString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : 'N/A'}</div>
                        {edlEntity?.id && (<div><strong>ID:</strong> {edlEntity.id}</div>)}
                      </div>
                    </div>
                    {hasSignificantEnhancement(edlEnhancement.body, edlEnhancement.enhanced_body) && (
                      <button
                        onClick={() => setEdlShowOriginal(prev => !prev)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '8px 12px',
                          marginBottom: '12px',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          textAlign: 'center',
                          color: 'var(--em3-color-text-inverse)',
                          background: edlShowOriginal ? 'var(--em3-color-success)' : 'var(--em3-color-danger)',
                          transition: 'background 0.2s ease'
                        }}
                      >
                        {edlShowOriginal
                          ? 'Context Enhancement Available \u2014 Click to show enhancement'
                          : 'Context Enhancement Active \u2014 Click to show original'}
                      </button>
                    )}
                    <EnhancedLogDisplay
                      originalText={edlEnhancement.body}
                      enhancedText={edlEnhancement.enhanced_body}
                      metadata={edlEnhancement.enhancement_metadata}
                      displayMode={edlShowOriginal ? 'original' : 'enhanced'}
                      onCrosslinkClick={meta => {
                        console.debug('[EnhancedDutyLogExplorer] onCrosslinkClick', meta);
                        // Close EDL Viewer modal on crosslink click
                        setEdlModalOpen(false);
                        setEdlEntity(null);
                        setEdlEnhancement(null);
                        handleCrosslinkSearchChange({
                          term: meta.searchTerm || meta.enhanced || '',
                          type: meta.type || ''
                        });
                      }}
                    />
                  </>
                ) : (
                  <div style={{ color: 'var(--em3-color-text-secondary)', fontStyle: 'italic' }}>Processing enhancement...</div>
                )}
        </AppModal>
      </div>
    </>
  );
}
